import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
import { setDateLocale } from '../utils/dateFormatter';
import en from './locales/en.json';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TranslationDict = Record<string, unknown>;

/** Recursively extract dot-path keys from a nested object type */
type DotPaths<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends Record<string, unknown>
    ? DotPaths<T[K], `${Prefix}${K}.`>
    : `${Prefix}${K}`;
}[keyof T & string];

/** All valid translation keys derived from en.json */
export type TranslationKey = DotPaths<typeof en>;

interface I18nInstance {
  language: string;
  changeLanguage: (code: string) => Promise<void>;
}

interface I18nContextValue {
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
  i18n: I18nInstance;
}

// ---------------------------------------------------------------------------
// Supported languages
// ---------------------------------------------------------------------------

export const supportedLanguages = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்' },
  { code: 'tl', name: 'Tagalog', nativeName: 'Tagalog' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia' },
  { code: 'zh', name: 'Chinese', nativeName: '中文' },
  { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt' },
  { code: 'my', name: 'Burmese', nativeName: 'မြန်မာ' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
] as const;

const STORAGE_KEY = 'weather-story-language';

// ---------------------------------------------------------------------------
// Translation cache & lazy loader
// ---------------------------------------------------------------------------

const translationCache: Record<string, TranslationDict> = { en };

async function loadTranslation(code: string): Promise<TranslationDict> {
  if (translationCache[code]) return translationCache[code];

  try {
    let mod: { default: TranslationDict };
    switch (code) {
      case 'hi':
        mod = await import('./locales/hi.json');
        break;
      case 'ta':
        mod = await import('./locales/ta.json');
        break;
      case 'tl':
        mod = await import('./locales/tl.json');
        break;
      case 'id':
        mod = await import('./locales/id.json');
        break;
      case 'zh':
        mod = await import('./locales/zh.json');
        break;
      case 'ms':
        mod = await import('./locales/ms.json');
        break;
      case 'th':
        mod = await import('./locales/th.json');
        break;
      case 'vi':
        mod = await import('./locales/vi.json');
        break;
      case 'my':
        mod = await import('./locales/my.json');
        break;
      case 'de':
        mod = await import('./locales/de.json');
        break;
      default:
        return en;
    }
    translationCache[code] = mod.default;
    return mod.default;
  } catch (err) {
    console.error(`Failed to load language: ${code}`, err);
    return en;
  }
}

// ---------------------------------------------------------------------------
// Dot-path resolver  e.g. "common.appName" → "Weathr Story"
// ---------------------------------------------------------------------------

function resolve(dict: TranslationDict, key: string): string | undefined {
  const parts = key.split('.');
  let current: unknown = dict;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : undefined;
}

function interpolate(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, k: string) =>
    vars[k] !== undefined ? String(vars[k]) : `{{${k}}}`
  );
}

// ---------------------------------------------------------------------------
// Detect initial language
// ---------------------------------------------------------------------------

function detectLanguage(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && supportedLanguages.some((l) => l.code === stored)) return stored;
  } catch {
    /* ignore */
  }

  const nav = navigator.language?.split('-')[0];
  if (nav && supportedLanguages.some((l) => l.code === nav)) return nav;

  return 'en';
}

// ---------------------------------------------------------------------------
// Dynamic font loading — only fetch Noto Sans for non-Latin scripts on demand
// ---------------------------------------------------------------------------

const SCRIPT_FONTS: Record<string, string> = {
  hi: 'Noto+Sans+Devanagari:wght@400;500;600;700',
  ta: 'Noto+Sans+Tamil:wght@400;500;600;700',
  th: 'Noto+Sans+Thai:wght@400;500;600;700',
  my: 'Noto+Sans+Myanmar:wght@400;500;600;700',
  zh: 'Noto+Sans+SC:wght@400;500;600;700',
};

const loadedFonts = new Set<string>();

function loadScriptFont(langCode: string): void {
  const family = SCRIPT_FONTS[langCode];
  if (!family || loadedFonts.has(langCode)) return;
  loadedFonts.add(langCode);

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${family}&display=swap`;
  document.head.appendChild(link);
}

// ---------------------------------------------------------------------------
// React context
// ---------------------------------------------------------------------------

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState(detectLanguage);
  const [dict, setDict] = useState<TranslationDict>(translationCache[language] || en);

  // Load non-English translation on mount if needed
  useEffect(() => {
    if (language !== 'en' && !translationCache[language]) {
      loadTranslation(language).then(setDict);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync side-effects whenever language changes
  useEffect(() => {
    setDateLocale(language);
    document.documentElement.setAttribute('lang', language);
    loadScriptFont(language);
    try {
      localStorage.setItem(STORAGE_KEY, language);
    } catch {
      /* ignore */
    }
  }, [language]);

  const changeLanguage = useCallback(async (code: string) => {
    const loaded = await loadTranslation(code);
    setDict(loaded);
    setLanguage(code);
  }, []);

  const t = useCallback(
    (key: TranslationKey, vars?: Record<string, string | number>): string => {
      const value = resolve(dict, key) ?? resolve(en, key) ?? key;
      return vars ? interpolate(value, vars) : value;
    },
    [dict]
  );

  const i18n: I18nInstance = { language, changeLanguage };

  return <I18nContext value={{ t, i18n }}>{children}</I18nContext>;
}

// ---------------------------------------------------------------------------
// Hook — drop-in replacement for react-i18next's useTranslation()
// ---------------------------------------------------------------------------

export function useTranslation() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useTranslation must be used within <I18nProvider>');
  return ctx;
}
